import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	Button,
	Empty,
	Input,
	Popconfirm,
	Select,
	Spin,
	Table,
	message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { deleteApi, getApi, postApi } from 'redux/apis';
import './index.scss';

// WS-14 (ENG-333): Name Mappings table — lists the company's saved CsvValueMapping
// aliases (both employee_name and qbo_time_name), shows each mapping's provenance,
// supports manual add (source 'manual') and delete. Local state + getApi/postApi/
// deleteApi, matching the QboTimeMismatchPanel pattern (no Redux slice for aliases).

const ALIASES_URL = '/employee-cost/import/name-aliases';

interface AliasRow {
	id: string;
	mappingType: string;
	csvValue: string;
	employeeId: string | null;
	employeeName: string | null;
	source: string;
	createdAt: string;
}

// source value -> colored-text label.
const SOURCE_BADGE: Record<string, { color: string; label: string }> = {
	import: { color: '#1677ff', label: 'Import' },
	qbo_time: { color: '#722ed1', label: 'QBO Time' },
	manual: { color: '#52c41a', label: 'Manual' },
};

const NameMappingsTable: FC = () => {
	const companyId = localStorage.getItem('companyId') || '';

	// Active employees for the manual add selector come from the already-loaded
	// employees list (tab 1 fetches it into this slice on mount).
	const { data: employeesData = [] } = useSelector(
		(state: any) => state.employeesTable || {}
	);

	const [rows, setRows] = useState<AliasRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [adding, setAdding] = useState(false);
	const [newCsvValue, setNewCsvValue] = useState('');
	const [newEmployeeId, setNewEmployeeId] = useState<string | undefined>(
		undefined
	);

	const fetchAliases = useCallback(async () => {
		if (!companyId) {
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			// Fetch both mapping types and merge into one list. Ordering is handled
			// client-side by the column sorters (default: Source Value A–Z).
			const [empRes, qboRes] = await Promise.all([
				getApi(ALIASES_URL, { companyId, mappingType: 'employee_name' }),
				getApi(ALIASES_URL, { companyId, mappingType: 'qbo_time_name' }),
			]);
			const combined: AliasRow[] = [
				...(empRes.data?.aliases ?? []),
				...(qboRes.data?.aliases ?? []),
			];
			setRows(combined);
		} catch {
			message.error('Failed to load name mappings. Please try again.');
		} finally {
			setLoading(false);
		}
	}, [companyId]);

	useEffect(() => {
		void fetchAliases();
	}, [fetchAliases]);

	const employeeOptions = useMemo(
		() =>
			(Array.isArray(employeesData) ? employeesData : [])
				.filter((e: any) => e.isActiveInApp)
				.map((e: any) => ({ value: e.id, label: e.fullName })),
		[employeesData]
	);

	const handleDelete = async (id: string) => {
		setDeletingId(id);
		try {
			await deleteApi(`${ALIASES_URL}/${id}`);
			setRows((prev) => prev.filter((r) => r.id !== id));
			message.success('Name mapping deleted');
		} catch {
			message.error('Failed to delete name mapping. Please try again.');
		} finally {
			setDeletingId(null);
		}
	};

	const handleAdd = async () => {
		const csvValue = newCsvValue.trim();
		if (!csvValue) {
			message.error('Enter a source value.');
			return;
		}
		if (!newEmployeeId) {
			message.error('Select an employee.');
			return;
		}
		setAdding(true);
		try {
			await postApi(ALIASES_URL, {
				companyId,
				mappingType: 'employee_name',
				source: 'manual',
				aliases: [{ csvValue, employeeId: newEmployeeId }],
			});
			// POST returns { upserted } (no row body), so re-fetch to get the
			// authoritative row (id, createdAt, joined employeeName, source).
			await fetchAliases();
			setNewCsvValue('');
			setNewEmployeeId(undefined);
			message.success('Name mapping added');
		} catch {
			message.error('Failed to add name mapping. Please try again.');
		} finally {
			setAdding(false);
		}
	};

	const columns: ColumnsType<AliasRow> = [
		{
			title: 'Source Value',
			dataIndex: 'csvValue',
			key: 'csvValue',
			className: 'bg-white',
			sorter: (a: AliasRow, b: AliasRow) =>
				a.csvValue.localeCompare(b.csvValue),
			defaultSortOrder: 'ascend' as const,
			showSorterTooltip: false,
		},
		{
			title: 'Matched Employee',
			dataIndex: 'employeeName',
			key: 'employeeName',
			className: 'bg-white',
			sorter: (a: AliasRow, b: AliasRow) =>
				(a.employeeName ?? '').localeCompare(b.employeeName ?? ''),
			showSorterTooltip: false,
			render: (value: string | null) =>
				value ?? <span className="name-mappings__muted">—</span>,
		},
		{
			title: 'Source',
			dataIndex: 'source',
			key: 'source',
			className: 'bg-white',
			width: '15%',
			sorter: (a: AliasRow, b: AliasRow) => a.source.localeCompare(b.source),
			showSorterTooltip: false,
			render: (value: string) => {
				const badge = SOURCE_BADGE[value] ?? { color: 'inherit', label: value };
				return (
					<span style={{ color: badge.color, fontWeight: 500 }}>
						{badge.label}
					</span>
				);
			},
		},
		{
			title: 'Date Added',
			dataIndex: 'createdAt',
			key: 'createdAt',
			className: 'bg-white',
			width: '18%',
			sorter: (a: AliasRow, b: AliasRow) =>
				new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
			showSorterTooltip: false,
			render: (value: string) => dayjs(value).format('MMM D, YYYY'),
		},
		{
			title: '',
			key: 'delete',
			className: 'bg-white',
			width: '10%',
			render: (_: unknown, row: AliasRow) => (
				<Popconfirm
					title="Delete this name mapping?"
					okText="Delete"
					cancelText="Cancel"
					okButtonProps={{ danger: true }}
					onConfirm={() => handleDelete(row.id)}
					// Render at body level so the confirm popup isn't clipped by the
					// settings body's overflow:hidden / the table's scroll container.
					getPopupContainer={() => document.body}
					// The Delete button sits near the right viewport edge; place the
					// popup to its left so it doesn't overshoot off-screen.
					placement="left"
				>
					<Button danger size="small" loading={deletingId === row.id}>
						Delete
					</Button>
				</Popconfirm>
			),
		},
	];

	return (
		<div className="name-mappings dynamic-table dynamic-table-new">
			<Table<AliasRow>
				dataSource={rows}
				columns={columns}
				rowKey="id"
				pagination={false}
				className="table-global"
				loading={loading}
				scroll={{ y: '50vh', x: 'max-content' }}
				locale={{
					emptyText: loading ? (
						<Spin />
					) : (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description="No name mappings saved yet. Mappings are created automatically during import and can be added manually here."
							style={{ padding: '40px 20px' }}
						/>
					),
				}}
			/>

			<div className="name-mappings__add-row">
				<Input
					className="name-mappings__add-input"
					placeholder="Source value (name from file / QBO)"
					value={newCsvValue}
					onChange={(e) => setNewCsvValue(e.target.value)}
					onPressEnter={() => void handleAdd()}
					aria-label="New mapping source value"
				/>
				<Select
					className="name-mappings__add-select"
					showSearch
					placeholder="Select employee…"
					value={newEmployeeId}
					onChange={(value: string) => setNewEmployeeId(value)}
					options={employeeOptions}
					filterOption={(input, option) =>
						String(option?.label ?? '')
							.toLowerCase()
							.includes(input.toLowerCase())
					}
					aria-label="New mapping employee"
				/>
				<Button
					type="primary"
					loading={adding}
					onClick={() => void handleAdd()}
					style={{ background: '#1a3c34', borderColor: '#1a3c34' }}
				>
					Add
				</Button>
			</div>
		</div>
	);
};

export default NameMappingsTable;
